/**
 * Drone-coverage pick arbitration (#348a).
 *
 * The Drone Operator Coverage layer draws a translucent FILL ellipse (the asset's
 * service radius) beneath an ambient quadcopter PIN. The fill is a passive
 * reachability visualisation — it must never STEAL a click from a fire / hotspot /
 * incident sitting inside the same coverage bubble. But MapView resolves a click
 * with a single `scene.pick`, which returns whichever primitive is topmost at that
 * pixel; when that primitive is the coverage fill, every higher-priority layer's
 * `pick()` misses (it's handed the ellipse, not the fire) and the incident becomes
 * unselectable.
 *
 * This module is the pure, Cesium-free seam MapView uses to DEMOTE the fill: keep
 * the topmost pick unless it is a coverage fill, in which case drill for any other
 * entity underneath and prefer it. The coverage PIN (id `drone:…`) is NOT demoted,
 * so clicking the pin still opens the coverage panel; the fill remains a valid
 * fallback when nothing else is beneath the cursor.
 *
 * Kept Cesium-free (operates on the structural `{ id: { id: string } }` shape a
 * picked Cesium entity exposes) so the arbitration logic is unit-testable without a
 * WebGL viewer.
 */

/** Entity-id prefix of the coverage FILL ellipse (see DroneCoverageLayer). */
export const DRONE_COVERAGE_FILL_PREFIX = 'dronecov:';

/** A picked Cesium object exposes its Entity as `.id`, whose own `.id` is the string id. */
interface PickedLike {
  id?: { id?: unknown } | null;
}

/**
 * True when a `scene.pick` / `scene.drillPick` result is the drone-coverage FILL
 * ellipse (entity id begins with `dronecov:`). The PIN (`drone:…`) and everything
 * else return false.
 */
export function isDroneCoverageFillPick(picked: unknown): boolean {
  const entityId = (picked as PickedLike | null | undefined)?.id?.id;
  return typeof entityId === 'string' && entityId.startsWith(DRONE_COVERAGE_FILL_PREFIX);
}

/**
 * Arbitrate a click pick so the coverage fill never occludes an incident beneath it.
 *
 * - If the topmost pick is NOT a coverage fill, return it unchanged (zero overhead —
 *   `drill` is only invoked when needed).
 * - If it IS the fill, walk the drilled (front-to-back) results and return the first
 *   that is not itself a coverage fill (a fire/hotspot/pin/etc. underneath wins).
 * - If nothing else is there, return the fill so a bare-bubble click still opens the
 *   coverage panel.
 *
 * Generic over the picked type so MapView keeps the exact type `scene.pick` returns.
 */
export function preferNonCoverageFillPick<T>(top: T, drill: () => T[]): T {
  if (!isDroneCoverageFillPick(top)) return top;
  for (const hit of drill()) {
    if (hit && !isDroneCoverageFillPick(hit)) return hit;
  }
  return top;
}
